<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import CardStageTracker from './CardStageTracker.vue';
import CardTasks from './CardTasks.vue';
import CardTaskDialog from './CardTaskDialog.vue';
import ScheduleVisitDialog from './ScheduleVisitDialog.vue';
import CardCloseMenu from './CardCloseMenu.vue';
import CardConversationLink from './CardConversationLink.vue';
import CardActivity from './CardActivity.vue';
import CardProperties from './CardProperties.vue';
import CardContactPanel from './CardContactPanel.vue';
import CardPropertyCode from './CardPropertyCode.vue';
import { useCardDetail } from '../useCardDetail';
import { useCardTaskTypes } from '../useCardTaskTypes';
import { SCHEDULED_VISIT_KEY, canScheduleVisit } from '../visitTask';

const props = defineProps({
  cardId: { type: Number, required: true },
  // Opens straight on one of the card's tasks (from "Minhas tarefas").
  taskId: { type: Number, default: null },
});

// `change` tells the board the ticket was edited, so it only reloads when needed.
const emit = defineEmits(['close', 'change']);

const store = useStore();
const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');
const labels = useMapGetter('labels/getLabels');
const {
  card,
  stages,
  activities,
  isNotFound,
  isChanged,
  load,
  moveTo,
  setPriority,
  toggleAssignee,
  setTeam,
  toggleLabel,
  resolve,
  cancel,
  deleteCard,
  reopen,
  addComment,
  editComment,
  deleteComment,
  createTask,
  updateTask,
  changeTaskStatus,
  deleteTask,
  scheduleVisit,
} = useCardDetail();
const { taskTypes, fetchTaskTypes } = useCardTaskTypes();

// A visit is added through the scheduling dialog, never through "Nova tarefa".
const newTaskTypes = computed(() =>
  taskTypes.value.filter(type => type.system_key !== SCHEDULED_VISIT_KEY)
);
const visitType = computed(() =>
  taskTypes.value.find(type => type.system_key === SCHEDULED_VISIT_KEY)
);

const isMaximized = ref(false);
const expandedTaskIds = ref(props.taskId ? [props.taskId] : []);
const taskDialogRef = ref(null);
const isTaskDialogOpen = ref(false);

const openTaskDialog = () => {
  isTaskDialogOpen.value = true;
  taskDialogRef.value.open();
};

const scheduleDialogRef = ref(null);
// The visit request being scheduled; none for a visit scheduled on the card on its own.
const scheduledRequest = ref(null);

// Shares the "Nova tarefa" flag: Escape closes the dialog, not the card.
const openScheduleDialog = request => {
  scheduledRequest.value = request;
  isTaskDialogOpen.value = true;
  scheduleDialogRef.value.open(
    request || { assignee: card.value.assignees[0] }
  );
};

// From "Nova tarefa": a visit the customer asked for is scheduled as such, so the card never
// holds an open request next to the visit that answers it.
const scheduleVisitOnCard = () =>
  openScheduleDialog(card.value.tasks.find(canScheduleVisit) || null);

const submitVisit = visit =>
  scheduledRequest.value
    ? scheduleVisit(scheduledRequest.value.id, visit)
    : createTask({
        card_task_type_id: visitType.value.id,
        due_at: visit.scheduledAt,
        assignee_id: visit.assigneeId,
      });

const { t } = useI18n();

const close = () => emit('close');

// Deleting a card is for administrators (CardPolicy#destroy?); the board reloads once the modal
// goes, as after any change.
const currentRole = useMapGetter('getCurrentRole');
const isAdministrator = computed(() => currentRole.value === 'administrator');
const deleteDialogRef = ref(null);

const askDelete = () => {
  isTaskDialogOpen.value = true;
  deleteDialogRef.value.open();
};

const confirmDelete = async () => {
  if (!(await deleteCard())) return;
  deleteDialogRef.value.close();
  useAlert(t('CARDS.SHOW.DELETED'));
  close();
};

watch(isChanged, () => emit('change'));

// Escape closes the "Nova tarefa" dialog first (the browser does that), not the whole card.
const onKeydown = event => {
  if (event.key === 'Escape' && !isTaskDialogOpen.value) close();
};

onMounted(() => {
  window.addEventListener('keydown', onKeydown);
  store.dispatch('agents/get');
  fetchTaskTypes();
  load(props.cardId);
});
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown));
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      data-testid="card-modal"
      class="fixed inset-0 z-50 grid place-items-center bg-n-alpha-black1 backdrop-blur-[4px]"
      :class="isMaximized ? 'p-0' : 'p-0 sm:p-6'"
      @click.self="close"
    >
      <section
        class="flex flex-col w-full h-full overflow-hidden shadow-xl bg-n-background outline outline-1 outline-n-container"
        :class="{
          'sm:rounded-2xl sm:max-w-[1280px] sm:h-[92vh]': !isMaximized,
        }"
        data-testid="card-modal-dialog"
      >
        <header
          class="flex items-center flex-shrink-0 gap-2 px-4 border-b sm:px-5 h-14 border-n-weak"
        >
          <h1
            class="min-w-0 mb-0 text-lg font-semibold truncate sm:text-xl text-n-slate-12"
            data-testid="card-modal-title"
          >
            {{ card?.name }}
          </h1>
          <CardPropertyCode
            v-if="card?.property_code"
            :code="card.property_code"
            class="!h-7 !text-sm"
          />
          <div class="flex items-center gap-1 ltr:ml-auto rtl:mr-auto">
            <template v-if="card">
              <Button
                v-if="card.status !== 'open'"
                :label="$t('CARDS.SHOW.REOPEN')"
                icon="i-lucide-rotate-ccw"
                color="slate"
                variant="outline"
                size="sm"
                class="ltr:mr-2 rtl:ml-2"
                data-testid="card-reopen"
                @click="reopen"
              />
              <CardCloseMenu
                v-else
                :status-labels="card.pipeline_status_labels"
                :deletable="isAdministrator"
                class="ltr:mr-2 rtl:ml-2"
                @resolve="resolve"
                @cancel="cancel"
                @delete="askDelete"
              />
              <Button
                v-if="card.status !== 'open' && isAdministrator"
                icon="i-lucide-trash-2"
                color="ruby"
                variant="ghost"
                size="sm"
                :title="$t('CARDS.SHOW.DELETE')"
                class="ltr:mr-2 rtl:ml-2"
                data-testid="card-delete"
                @click="askDelete"
              />
            </template>
            <button
              type="button"
              data-testid="card-modal-maximize"
              class="hidden sm:grid rounded-lg size-8 place-content-center text-n-slate-11 hover:bg-n-alpha-2"
              :title="
                isMaximized
                  ? $t('CARDS.MODAL.RESTORE')
                  : $t('CARDS.MODAL.MAXIMIZE')
              "
              @click="isMaximized = !isMaximized"
            >
              <span
                class="size-4"
                :class="
                  isMaximized ? 'i-lucide-minimize-2' : 'i-lucide-maximize-2'
                "
              />
            </button>
            <button
              type="button"
              data-testid="card-modal-close"
              class="grid rounded-lg size-8 place-content-center text-n-slate-11 hover:bg-n-alpha-2"
              :title="$t('CARDS.MODAL.CLOSE')"
              @click="close"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>
        </header>

        <!-- On a narrow screen the side panel stacks under the content and the two scroll as one. -->
        <div
          v-if="card"
          class="flex flex-col flex-1 min-h-0 overflow-y-auto md:flex-row md:overflow-hidden"
        >
          <main class="min-w-0 md:flex-1 md:overflow-y-auto">
            <!-- The steps stay pinned over the card's content as it scrolls. -->
            <div
              class="sticky top-0 z-20 px-4 pt-5 pb-4 border-b sm:px-8 bg-n-background border-n-weak"
            >
              <CardStageTracker
                :card="card"
                :stages="stages"
                class="max-w-3xl mx-auto"
                @move="moveTo"
              />
            </div>
            <div
              class="flex flex-col max-w-3xl gap-8 px-4 py-6 mx-auto sm:px-8"
            >
              <CardTasks
                v-model:expanded-ids="expandedTaskIds"
                :tasks="card.tasks"
                :agents="agents"
                :can-schedule-visit="!!visitType"
                :delete-task="deleteTask"
                @create="openTaskDialog"
                @schedule-visit="scheduleVisitOnCard"
                @status="
                  (task, transition) => changeTaskStatus(task.id, transition)
                "
                @update="(task, attributes) => updateTask(task.id, attributes)"
                @schedule="openScheduleDialog"
              />
              <CardConversationLink :card="card" />
              <CardActivity
                :card="card"
                :activities="activities"
                :add-comment="addComment"
                :edit-comment="editComment"
                :delete-comment="deleteComment"
              />
            </div>
          </main>

          <aside
            class="flex flex-col flex-shrink-0 w-full gap-6 p-5 overflow-x-hidden border-t border-n-weak md:border-t-0 md:overflow-y-auto md:w-[26rem] md:ltr:border-l md:rtl:border-r"
          >
            <div>
              <h2 class="mb-2 text-xs font-medium uppercase text-n-slate-10">
                {{ $t('CARDS.SHOW.PROPERTIES') }}
              </h2>
              <CardProperties
                :card="card"
                :stages="stages"
                :agents="agents"
                :teams="teams"
                :labels="labels"
                @move="moveTo"
                @priority="setPriority"
                @assignee="toggleAssignee"
                @team="setTeam"
                @label="toggleLabel"
              />
            </div>
            <CardContactPanel
              v-if="card.contacts.length"
              :key="card.contacts[0].id"
              :contact-id="card.contacts[0].id"
              class="pt-5 -mx-5 border-t border-n-weak"
            />
          </aside>
        </div>
        <p
          v-else-if="isNotFound"
          class="py-16 mb-0 text-sm text-center text-n-slate-11"
        >
          {{ $t('CARDS.SHOW.NOT_FOUND') }}
        </p>
        <CardTaskDialog
          v-if="card"
          ref="taskDialogRef"
          :task-types="newTaskTypes"
          :agents="agents"
          :default-assignee-id="card.assignees[0]?.id"
          :has-contact="card.contacts.length > 0"
          :submit="createTask"
          @close="isTaskDialogOpen = false"
        />
        <ScheduleVisitDialog
          v-if="card"
          ref="scheduleDialogRef"
          :agents="agents"
          :submit="submitVisit"
          @close="isTaskDialogOpen = false"
        />
        <Dialog
          v-if="card"
          ref="deleteDialogRef"
          type="alert"
          :title="$t('CARDS.SHOW.DELETE_TITLE', { name: card.name })"
          :description="$t('CARDS.SHOW.DELETE_DESCRIPTION')"
          :confirm-button-label="$t('CARDS.SHOW.DELETE')"
          @confirm="confirmDelete"
          @close="isTaskDialogOpen = false"
        />
      </section>
    </div>
  </TeleportWithDirection>
</template>
